import { format as formatDate, isDate, Locale } from "date-fns";
import { enUS, nl, th } from "date-fns/locale";
import i18n from "i18next";

const locales: { [key: string]: Locale } = { enUS, nl, th }; // used to look up the required locale

const mapToDateFnsLocale: { [key: string]: string } = {
    "en-US": "enUS",
    "nl-NL": "nl",
    "th-TH": "th",
};

const datePluralSuffixes = new Map([
    ["one", "st"],
    ["two", "nd"],
    ["few", "rd"],
    ["other", "th"],
]);

const formatOrdinals = (pluralRules: Intl.PluralRules, value: number) => {
    const rule = pluralRules.select(value);
    const suffix = datePluralSuffixes.get(rule);
    return `${value}${suffix}`;
};

i18n.init({
    partialBundledLanguages: true,
    ns: [],
    resources: {},
    fallbackLng: "en-US",
    saveMissing: true,
    parseMissingKeyHandler: () => null,
    missingKeyNoValueFallbackToKey: true,
    interpolation: {
        format: (value: any, format?: string, lng?: string): any => {
            if (isDate(value) && format && lng) {
                // since intl knows how to translate months names use that for this commonly used format.
                // @docs
                //      https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
                //      https://date-fns.org/v4.1.0/docs/format

                if (format === "MMMM yyyy") {
                    return `${new Intl.DateTimeFormat(lng, {
                        month: "long",
                        year: "numeric",
                    }).format(value)}`;
                } else if (format === "do MMMMM yyyy") {
                    return `${formatOrdinals(new Intl.PluralRules("en-US", { type: "ordinal" }), value.getDay())} ${new Intl.DateTimeFormat(
                        lng,
                        {
                            month: "narrow",
                            year: "numeric",
                        }
                    ).format(value)}`;
                } else if (format === "d MMMM yyyy") {
                    return `${new Intl.DateTimeFormat(lng, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    }).format(value)}`;
                } else if (format === "MMMM do yyyy") {
                    return [
                        new Intl.DateTimeFormat(lng, {
                            month: "long",
                        }).format(value),
                        formatOrdinals(new Intl.PluralRules("en-US", { type: "ordinal" }), value.getDay()),
                        new Intl.DateTimeFormat(lng, {
                            year: "numeric",
                        }).format(value),
                    ].join(" ");
                }

                const locale = locales[mapToDateFnsLocale[lng]];
                const formattedDate = formatDate(value, format, { locale });

                // Capitalize the first letter of the formatted month
                return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
            }
        },
    },
});

export default i18n;
